import { DATE_VALUES } from 'utils/dateUtils'
import { URL_CONST } from 'utils/urlConsts'

const STORYBOOK_PATHNAME_KEY = '__STORYBOOK_PATHNAME__'

const getPathname = () => {
  if (typeof window === 'undefined') return ''
  if (window[STORYBOOK_PATHNAME_KEY]) return window[STORYBOOK_PATHNAME_KEY]
  return window.location.pathname
}

export const matchURL = (url) => {
  const pathname = getPathname()
  return pathname === url || `${pathname}/` === url || pathname === `${url}/`
}

/**
 * @example
 * // If current URL is '/awareness-center/wizard-campaign'
 * matchURLStartsWith('/awareness-center')                 // returns true
 * matchURLStartsWith('/awareness-center/wizard-campaign') // returns true
 * matchURLStartsWith('/wizard-campaign')                  // returns false
 * matchURLStartsWith('/dashboard')                        // returns false
 */
export const matchURLStartsWith = (urlPart) => getPathname().startsWith(urlPart)

const getParamsFromArray = (name, arr) => {
  if (!arr.length) return ''

  return arr.reduce(
    (prev, curr, i) => `${prev}${i === 0 ? '' : '&'}${name}=${encodeURIComponent(curr)}`,
    ''
  )
}

/**
 * Builds a URL from a given string and params object.
 *
 * @param {string} url
 * @param {object} params
 * @param {boolean} duplicateArrayKeys
 *
 * Example:
 * buildURL('/mailboxes/emails', { emailIds: ['1', '2', '3'] }, true)
 * returns: '/mailboxes/emails?emailIds=1&emailIds=2&emailIds=3'
 */
export const buildURL = (url, params = {}, duplicateArrayKeys = false) => {
  // clear params object from empty strings and undefined content
  let parsedUrl = url
  const existingParams = Object.keys(params).reduce((prev, curr) => {
    if (params[curr] !== '' && params[curr] !== undefined && params[curr] !== null)
      return { ...prev, [curr]: params[curr] }
    return prev
  }, {})

  // build the URL
  if (Object.keys(existingParams).length) {
    Object.keys(existingParams).forEach((key) => {
      const symbol = parsedUrl.indexOf('?') === -1 ? '?' : '&'

      if (duplicateArrayKeys && Array.isArray(existingParams[key])) {
        if (existingParams[key].length === 0) return
        parsedUrl += `${symbol}${getParamsFromArray(key, existingParams[key])}`
      } else {
        parsedUrl += `${symbol}${key}=${encodeURIComponent(existingParams[key])}`
      }
    })
  }

  return parsedUrl
}

/**
 * Builds a URL from a given string and params object. This is a version of buildURL handle arrays differently.
 * In case of arrays, the array is JSON.stringified and encoded.
 * This address issue below:
 *
 * 'foo' === ['foo'].join(',')
 *
 * We want this for frontend related stuff, like deep dive links.
 *  Since it is more easier than create map of each key and how it should be handled
 *
 * @param {string} url
 * @param {object} params
 *
 */
const buildURLv2 = (url, params) => {
  const urlParams = []

  for (const [key, value] of Object.entries(params)) {
    if (value == undefined) continue
    if (Array.isArray(value)) {
      const encodedValue = encodeURIComponent(JSON.stringify(value))
      urlParams.push(`${key}=${encodedValue}`)
    } else {
      const encodedValue = encodeURIComponent(value)
      urlParams.push(`${key}=${encodedValue}`)
    }
  }

  const urlWithoutTrailingSlash = url.replace(/\/$/, '')
  const urlWithTrailingSlash = urlWithoutTrailingSlash.endsWith('/')
    ? urlWithoutTrailingSlash
    : `${urlWithoutTrailingSlash}/`

  const constructedUrl = `${urlWithTrailingSlash}?${urlParams.join('&')}`

  return constructedUrl
}

export const buildInvestigationUrl = (date = DATE_VALUES.LAST_24_HOURS, queryParams = {}) =>
  buildURLv2(URL_CONST.investigation.threatLog, {
    ...queryParams,
    emailDateFilter: date,
    logDateFilter: DATE_VALUES.ALL_TIME,
  })
